import * as preact from "preact";

import * as editCardDialog from "editCardDialog";
import * as dateUtils from "shared/dateUtils";
import * as languageMap from "shared/languageMap";
import * as spacedRepetition from "shared/spacedRepetition";
import * as utils from "shared/utils";
import ReaderViewStore from "stores/ReaderViewStore";
import UserWordStore, { formatContextSegments } from "stores/UserWordStore";
import { positionWordPopUp } from "views/wordPopUpPositioning";

interface RemoteUserWordsStoreLike {
  length: number;
  viewJSON: { value: any[] };
  firstWord(): UserWordStore | undefined;
  getWord(id: string): UserWordStore | undefined;
  fetch(options: { data: any; success?: () => void; error?: () => void }): any;
  onChange(handler: () => void): () => void;
}

interface NewContext {
  text: string;
  bookID?: any;
}

export interface WordPopUpOptions {
  targetElement: JQuery<HTMLElement>;
  containerElement: JQuery<HTMLElement>;
  phrases: string[];
  remoteUserWordsFactory: () => RemoteUserWordsStoreLike;
  userModel?: any;
  readerViewStore: ReaderViewStore;
  newContext?: NewContext;
  fetchedWordCallback?: (userWord: UserWordStore | undefined) => void;
}

interface ComponentProps extends WordPopUpOptions {
  mountEl: HTMLElement;
  onClose: () => void;
}

interface ComponentState {
  fetching: boolean;
}

const renderContextSegments = (
  contextText: string,
  word: string
): preact.ComponentChildren =>
  formatContextSegments(contextText, word).map((segment, index) =>
    segment.type === "strong" ? (
      <strong key={index}>{segment.value}</strong>
    ) : (
      <preact.Fragment key={index}>{segment.value}</preact.Fragment>
    )
  );

class WordPopUpComponent extends preact.Component<
  ComponentProps,
  ComponentState
> {
  state: ComponentState = { fetching: true };
  remoteUserWords: RemoteUserWordsStoreLike;
  /**
   * The fetch can't be aborted, so a superseded popup's late completion is
   * ignored here instead. Without this a slow lookup landing after the user
   * has moved on would push its obsolete word into the reader's dictionary.
   */
  unmounted = false;
  disposeRemoteUserWordsChange?: () => void;
  disposePageLayoutUpdated?: () => void;
  popUpRef: preact.RefObject<HTMLDivElement> = preact.createRef();
  dismissAreaRef: preact.RefObject<HTMLDivElement> = preact.createRef();

  constructor(props: ComponentProps) {
    super(props);
    this.remoteUserWords = props.remoteUserWordsFactory();
  }

  componentDidMount() {
    this.remoteUserWords.fetch({
      data: {
        exactPhrasesSearch: this.props.phrases.join("|"),
      },
      success: () => {
        if (this.unmounted) return;
        this.disposeRemoteUserWordsChange = this.remoteUserWords.onChange(
          this.onCollectionChanged
        );
        this.setState({ fetching: false });
        if (this.props.fetchedWordCallback) {
          this.props.fetchedWordCallback(this.remoteUserWords.firstWord());
        }
      },
    });

    this.disposePageLayoutUpdated =
      this.props.readerViewStore.onPageLayoutUpdated(this.updatePosition);
    this.updatePosition();
  }

  componentWillUnmount() {
    this.unmounted = true;
    this.disposeRemoteUserWordsChange?.();
    this.disposePageLayoutUpdated?.();
  }

  componentDidUpdate() {
    this.updatePosition();
  }

  onCollectionChanged = () => {
    if (this.remoteUserWords.length === 0 && !this.state.fetching) {
      // After all words are deleted from the popup we still want to render
      // the "All words deleted" state — handled by render() reading
      // remoteUserWords.length.
    }
    this.forceUpdate();
  };

  updatePosition = () => {
    positionWordPopUp({
      targetElement: this.props.targetElement,
      containerElement: this.props.containerElement,
      mountEl: this.props.mountEl,
      popUp: this.popUpRef.current,
      dismissArea: this.dismissAreaRef.current,
    });
  };

  getUserWordFromEvent = (event: Event): UserWordStore | undefined => {
    const target = event.target as HTMLElement;
    const idEl = target.closest("[data-id]") as HTMLElement | null;
    const id = idEl?.getAttribute("data-id");
    if (!id) return undefined;
    return this.remoteUserWords.getWord(id);
  };

  handleClose = (event: Event) => {
    event.preventDefault();
    this.props.onClose();
  };

  handleEdit = (event: Event) => {
    const userWord = this.getUserWordFromEvent(event);
    if (!userWord) return;
    editCardDialog.show(userWord, {
      title: "Edit Word / Phrase",
    });
  };

  handleDelete = async (event: Event) => {
    const userWord = this.getUserWordFromEvent(event);
    if (!userWord) return;
    await userWord.deleteUserWord();
    if (this.remoteUserWords.length === 0) {
      this.props.onClose();
    }
  };

  handleRecall = (event: Event) => {
    const userWord = this.getUserWordFromEvent(event);
    if (!userWord) return;
    userWord.recall({
      recallEase: 4,
      difficulty: spacedRepetition.READING_DIFFICULTY,
      source: "remembered",
    });
    this.props.onClose();
  };

  handleAddContext = (event: Event) => {
    const userWord = this.getUserWordFromEvent(event);
    if (!userWord || !this.props.newContext) return;
    const contexts = (userWord.state.value.contexts || []).map(
      (context: any) => {
        // un-populate bookID
        if (context.bookID && context.bookID._id) {
          return { ...context, bookID: context.bookID._id };
        }
        return { ...context };
      }
    );
    contexts.push(this.props.newContext);
    userWord.save({ contexts }, { patch: true });
  };

  render() {
    const { fetching } = this.state;
    const userWords = this.remoteUserWords.viewJSON.value;
    const noWords = userWords.length === 0;

    return (
      <preact.Fragment>
        <div class="yourWordPopUp" ref={this.popUpRef}>
          <div class="closeDialogButton" onClick={this.handleClose}>
            &nbsp;x&nbsp;
          </div>
          {fetching
            ? "Fetching..."
            : noWords
              ? "All words deleted"
              : userWords.map((userWord: any) => this.renderUserWord(userWord))}
        </div>
        <div
          class="dismissArea"
          ref={this.dismissAreaRef}
          onClick={this.handleClose}
        />
      </preact.Fragment>
    );
  }

  renderUserWord(userWord: any) {
    const isPhrase = utils.notWordRegExp().test(userWord.word);
    const type = isPhrase ? "phrase" : "word";
    const allTranslationsString = [
      userWord.translation,
      ...(userWord.alternativeTranslations || []),
    ].join(" / ");
    const rtl = languageMap.map[userWord.language]?.direction === "RTL";

    let newContext = this.props.newContext;
    const contexts = userWord.contexts || [];
    if (
      newContext &&
      contexts.some((context: any) => context.text === newContext!.text)
    ) {
      newContext = undefined;
    }

    const showRecallButton =
      !userWord.recalledThisSession &&
      spacedRepetition.isDue(
        userWord.nextDate,
        new Date(),
        dateUtils.getTimezoneOffset()
      );

    return (
      <preact.Fragment key={userWord._id}>
        <div class="buttons">
          {showRecallButton && (
            <button
              class="recallWord"
              data-id={userWord._id}
              onClick={this.handleRecall}
            >
              <i class="icon-ok"></i> I remembered
            </button>
          )}
          <button
            class="editWord"
            data-id={userWord._id}
            style={{ paddingLeft: 15, paddingRight: 15 }}
            onClick={this.handleEdit}
          >
            <i class="icon-pencil"></i> Edit {type}
          </button>
          <button
            class="deleteWord"
            data-id={userWord._id}
            onClick={this.handleDelete}
          >
            <i class="icon-remove"></i> Delete {type}
          </button>
        </div>

        <h3>
          {userWord.word}&nbsp;<small>-&nbsp;{allTranslationsString}</small>
        </h3>

        <ul
          dir={rtl ? "rtl" : undefined}
          style={{
            listStyle: "disc",
            ...(rtl ? { marginRight: 20 } : { marginLeft: 20 }),
          }}
        >
          {contexts.map((context: any, index: number) => (
            <li key={index} dir={rtl ? "rtl" : undefined}>
              {renderContextSegments(context.text, userWord.word)}
            </li>
          ))}
          {newContext && (
            <li>
              <button
                style={{ textAlign: "left" }}
                class="addContext"
                data-id={userWord._id}
                dir="ltr"
                onClick={this.handleAddContext}
              >
                <h3>+ Add this context</h3>
                <div
                  style={{
                    padding: 8,
                    ...(rtl ? { textAlign: "right" } : {}),
                  }}
                  dir={rtl ? "rtl" : undefined}
                >
                  {renderContextSegments(newContext.text, userWord.word)}
                </div>
              </button>
            </li>
          )}
        </ul>
      </preact.Fragment>
    );
  }
}

export { WordPopUpComponent };
